<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getImportSession } from '@/api/api.js'
import { useToast } from '@/composables/useToast'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import StepUpload from '@/components/import/StepUpload.vue'
import StepColumnMapping from '@/components/import/StepColumnMapping.vue'
import StepCategoryHierarchy from '@/components/import/StepCategoryHierarchy.vue'
import StepPreview from '@/components/import/StepPreview.vue'
import StepResults from '@/components/import/StepResults.vue'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const toast = useToast()

// Shared wizard state
const session = ref(null)
const columnMapping = ref({})
const defaults = ref({})
const categoryHierarchy = ref({})
const itemTypeOverrides = ref({})
const loading = ref(false)
const loadError = ref('')
const stepOverride = ref(null) // used to advance past reviewing → step 5

// Derive current step from session status
const STATUS_TO_STEP = {
  uploading: 1,
  parsing: 1,
  parsed: 2,
  mapping: 2,
  hierarchy: 3,
  reviewing: 4,
  importing: 5,
  done: 5,
  failed: 5
}

const STEPS = computed(() => [
  { num: 1, label: t('import.stepUpload') },
  { num: 2, label: t('import.stepColumnMapping') },
  { num: 3, label: t('import.stepCategories') },
  { num: 4, label: t('import.stepPreviewEdit') },
  { num: 5, label: t('import.stepImport') },
])

const currentStep = computed(() => {
  if (stepOverride.value !== null) return stepOverride.value
  if (!session.value) return 1
  if (session.value.status === 'failed' && !session.value.has_parsed_data) return 1
  return STATUS_TO_STEP[session.value.status] ?? 2
})

const sessionId = computed(() => route.params.sessionId)
const isNew = computed(() => sessionId.value === 'new' || !sessionId.value)

onMounted(async () => {
  if (!isNew.value) {
    await loadSession()
  }
})

watch(
  () => sessionId.value,
  async (newId) => {
    if (newId && newId !== 'new') await loadSession()
  }
)

async function loadSession() {
  loading.value = true
  loadError.value = ''
  stepOverride.value = null
  try {
    const res = await getImportSession(sessionId.value)
    session.value = res
    columnMapping.value = session.value.column_mapping || {}
    defaults.value = session.value.defaults || {}
    categoryHierarchy.value = session.value.category_hierarchy || {}
    itemTypeOverrides.value = session.value.item_type_overrides || {}
  } catch {
    loadError.value = t('import.wizardLoadError')
    toast.error(t('import.wizardLoadErrorToast'))
  } finally {
    loading.value = false
  }
}

// Called when StepUpload emits a newly created session
function onSessionCreated(newSession) {
  session.value = newSession
  router.replace(`/import/${newSession.id}`)
}

// Called when each step advances
function onMappingAdvance({ column_mapping, defaults: defs }) {
  columnMapping.value = column_mapping
  defaults.value = defs
  session.value = { ...session.value, status: 'hierarchy', column_mapping, defaults: defs }
}

function onHierarchyAdvance({ category_hierarchy }) {
  categoryHierarchy.value = category_hierarchy
  session.value = { ...session.value, status: 'reviewing', category_hierarchy }
}

function onPreviewAdvance({ item_type_overrides }) {
  itemTypeOverrides.value = item_type_overrides
  session.value = { ...session.value, status: 'reviewing', item_type_overrides, result_summary: null, error_message: '' }
  stepOverride.value = 5
}

function restart() {
  router.push('/import/new')
  session.value = null
}

function backToPreview() {
  // Reset session status to 'reviewing' so the wizard shows step 4
  session.value = { ...session.value, status: 'reviewing' }
  stepOverride.value = null
}

function goBack() {
  router.push('/import')
}

const pageTitle = computed(() => {
  const step = STEPS.value.find((s) => s.num === currentStep.value)
  return step ? t('import.wizardTitle', { step: step.label }) : t('import.importItems')
})
</script>

<template>
  <div class="page-view">
    <BasePageHeader :title="pageTitle" @back="goBack" />

    <!-- Step progress bar -->
    <div class="flex items-center gap-0 px-4 pt-4 pb-2">
      <template v-for="(step, i) in STEPS" :key="step.num">
        <div class="flex flex-col items-center gap-1 flex-1">
          <div
            class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors"
            :class="
              currentStep >= step.num
                ? 'bg-(--color-accent) text-(--color-primary)'
                : 'bg-(--color-surface-2) text-(--color-muted) border border-(--color-border)'
            "
          >
            {{ step.num }}
          </div>
          <span
            class="text-xs hidden sm:block"
            :class="currentStep >= step.num ? 'text-(--color-accent)' : 'text-(--color-muted)'"
            >{{ step.label }}</span
          >
        </div>
        <div
          v-if="i < STEPS.length - 1"
          class="h-0.5 flex-1 mb-5"
          :class="currentStep > step.num ? 'bg-(--color-accent)' : 'bg-(--color-border)'"
        />
      </template>
    </div>

    <div class="p-4">
      <!-- Loading session -->
      <div v-if="loading" class="flex justify-center py-12">
        <span
          class="inline-block w-6 h-6 rounded-full border-2 border-(--color-accent) border-t-transparent animate-spin"
        />
      </div>

      <!-- Load error -->
      <div v-else-if="loadError" class="state-empty error">{{ loadError }}</div>

      <!-- Step 1: Upload -->
      <template v-else-if="currentStep === 1">
        <StepUpload :session="session" @session-created="onSessionCreated" />
      </template>

      <!-- Step 2: Column Mapping -->
      <template v-else-if="currentStep === 2 && session">
        <StepColumnMapping :session="session" @advance="onMappingAdvance" />
      </template>

      <!-- Step 3: Category Hierarchy -->
      <template v-else-if="currentStep === 3 && session">
        <StepCategoryHierarchy
          :session="session"
          :column-mapping="columnMapping"
          @advance="onHierarchyAdvance"
        />
      </template>

      <!-- Step 4: Interactive Preview -->
      <template v-else-if="currentStep === 4 && session">
        <StepPreview
          :session="session"
          :column-mapping="columnMapping"
          :defaults="defaults"
          :category-hierarchy="categoryHierarchy"
          @advance="onPreviewAdvance"
        />
      </template>

      <!-- Step 5: Results -->
      <template v-else-if="currentStep === 5 && session">
        <StepResults :session="session" @restart="restart" @back-to-preview="backToPreview" />
      </template>
    </div>
  </div>
</template>
